import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loading", "results", "refreshButton", "autoCorrectButton"]

  connect() {
    // Controller verbunden - Validierung wird nur auf Knopfdruck ausgeführt
  }

  startValidation() {
    // Zeige Loading-Zustand
    this.loadingTarget.classList.remove('hidden')
    this.resultsTarget.classList.add('hidden')
    this.refreshButtonTarget.classList.add('hidden')
    this.autoCorrectButtonTarget.classList.add('hidden')

    // Führe Validierung via AJAX aus
    fetch(`/de/backend/requisitions/${this.getRequisitionId()}/validate_competencies`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => response.json())
    .then(data => {
      this.showResults(data)
    })
    .catch(error => {
      this.showError('Validierung fehlgeschlagen. Bitte versuche es erneut.')
    })
  }

  refreshValidation() {
    this.startValidation()
  }

  autoCorrect() {
    // Zeige Loading-Zustand
    this.loadingTarget.classList.remove('hidden')
    this.resultsTarget.classList.add('hidden')

    // Führe Auto-Korrektur via AJAX aus
    fetch(`/de/backend/requisitions/${this.getRequisitionId()}/auto_correct_competencies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showSuccess('Auto-Korrektur erfolgreich!')
        // Lade die Seite neu um die Änderungen zu sehen
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        this.showError('Auto-Korrektur fehlgeschlagen.')
      }
    })
    .catch(error => {
      this.showError('Auto-Korrektur fehlgeschlagen. Bitte versuche es erneut.')
    })
  }

  showResults(data) {
    // Verstecke Loading-Zustand
    this.loadingTarget.classList.add('hidden')
    this.resultsTarget.classList.remove('hidden')
    this.refreshButtonTarget.classList.remove('hidden')
    this.autoCorrectButtonTarget.classList.remove('hidden')

    // Erstelle die Validierungsergebnisse
    let resultsHtml = ''
    
    if (data.valid && (!data.suggestions || data.suggestions.length === 0)) {
      // Alles korrekt
      resultsHtml = `
        <div class="text-center py-8">
          <i class="fas fa-check-circle text-4xl text-green-500 mb-4"></i>
          <p class="text-gray-600">Alle Kompetenz-Anforderungen sind korrekt konfiguriert.</p>
        </div>
      `
    } else {
      // Fehler oder Vorschläge vorhanden
      if (data.errors && data.errors.length > 0) {
        resultsHtml += `
          <div class="mb-6">
            <h4 class="text-md font-medium text-red-700 mb-3">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              Validierungsfehler (${data.errors.length})
            </h4>
            <div class="space-y-3">
        `
        
        data.errors.forEach(error => {
          resultsHtml += `
            <div class="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
              <i class="fas fa-times-circle text-red-500 mt-1 mr-3"></i>
              <div class="flex-1">
                <p class="text-sm text-red-800 font-medium">${error.competency_title || 'Unbekannte Kompetenz'}</p>
                <p class="text-sm text-red-600">${error.message}</p>
                ${error.requirement_id ? `<p class="text-xs text-red-500 mt-1">Requirement ID: ${error.requirement_id}</p>` : ''}
              </div>
            </div>
          `
        })
        
        resultsHtml += `
            </div>
          </div>
        `
      }
      
      if (data.suggestions && data.suggestions.length > 0) {
        resultsHtml += `
          <div class="mb-6">
            <h4 class="text-md font-medium text-yellow-700 mb-3">
              <i class="fas fa-lightbulb mr-2"></i>
              Verbesserungsvorschläge (${data.suggestions.length})
            </h4>
            <div class="space-y-4">
        `
        
        data.suggestions.forEach(suggestion => {
          resultsHtml += `
            <div class="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
              <div class="flex items-start">
                <i class="fas fa-search text-yellow-500 mt-1 mr-3"></i>
                <div class="flex-1">
                  <p class="text-sm font-medium text-yellow-800">Vorschlag: ${suggestion.message || 'Verbesserung verfügbar'}</p>
                </div>
              </div>
            </div>
          `
        })
        
        resultsHtml += `
            </div>
          </div>
        `
      }
      
      if (data.summary) {
        resultsHtml += `
          <div class="mt-6 pt-4 border-t border-gray-200">
            <h4 class="text-sm font-medium text-gray-700 mb-3">Zusammenfassung</h4>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Gesamt Requirements:</span>
                <span class="font-medium">${data.summary.total_requirements || 0}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Fehler:</span>
                <span class="font-medium text-red-600">${data.summary.errors_count || 0}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Vorschläge:</span>
                <span class="font-medium text-yellow-600">${data.summary.suggestions_count || 0}</span>
              </div>
            </div>
          </div>
        `
      }
    }
    
    // Aktualisiere das DOM
    this.resultsTarget.innerHTML = resultsHtml
  }

  showSuccess(message) {
    // Zeige Erfolgsmeldung
    this.loadingTarget.classList.add('hidden')
    this.resultsTarget.classList.remove('hidden')
    
    // Erstelle eine einfache Erfolgsmeldung
    const successDiv = document.createElement('div')
    successDiv.className = 'text-center py-8'
    successDiv.innerHTML = `
      <i class="fas fa-check-circle text-4xl text-green-500 mb-4"></i>
      <p class="text-gray-600">${message}</p>
    `
    this.resultsTarget.innerHTML = ''
    this.resultsTarget.appendChild(successDiv)
  }

  showError(message) {
    // Zeige Fehlermeldung
    this.loadingTarget.classList.add('hidden')
    this.resultsTarget.classList.remove('hidden')
    
    // Erstelle eine einfache Fehlermeldung
    const errorDiv = document.createElement('div')
    errorDiv.className = 'text-center py-8'
    errorDiv.innerHTML = `
      <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
      <p class="text-gray-600">${message}</p>
    `
    this.resultsTarget.innerHTML = ''
    this.resultsTarget.appendChild(errorDiv)
  }

  getRequisitionId() {
    // Extrahiere die Requisition ID aus der URL
    const urlParts = window.location.pathname.split('/')
    return urlParts[urlParts.indexOf('requisitions') + 1]
  }
} 